import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
    ArrowLeft, LayoutGrid, Building2, Users, UserCog, ShieldCheck, Wallet, ScrollText, LifeBuoy,
    UserCheck, Zap, Menu, ChartNoAxesCombined,
} from 'lucide-react';
import { Drawer } from '../../packages/ui';
import { useTheme } from '../../hooks/useTheme';
import { KaziAtlasLogo } from '../KaziAtlasLogo';
import { ImpersonationBanner } from './Banners';
import { AccountPanel } from './Sidebar';
import { isItemActive } from './nav';
import './Shell.css';

interface PlatformItem { label: string; to: string; icon: LucideIcon }
interface PlatformGroup { label: string; items: PlatformItem[] }

/**
 * Platform console navigation (handoff §5, design spec "Platform navigation"),
 * mapped onto the existing /admin/* pages. A one-item group renders as its
 * link alone. Route guards enforce access, not this list.
 */
export const PLATFORM_NAV: PlatformGroup[] = [
    { label: 'Overview', items: [{ label: 'Overview', to: '/admin/overview', icon: LayoutGrid }] },
    { label: 'Website traffic', items: [{ label: 'Website traffic', to: '/admin/website-traffic', icon: ChartNoAxesCombined }] },
    { label: 'Tenants', items: [{ label: 'Tenants', to: '/admin/tenants', icon: Building2 }] },
    // For a superadmin UsersPage is the cross-tenant directory (tenant filter, OTP exemption).
    { label: 'Users', items: [{ label: 'Users', to: '/admin/users', icon: Users }] },
    {
        label: 'Groups & Permissions', items: [
            { label: 'Groups & roles', to: '/admin/roles', icon: UserCog },
            { label: 'Permission catalogue', to: '/admin/permissions', icon: ShieldCheck },
        ],
    },
    { label: 'Billing & AI usage', items: [{ label: 'Billing & AI usage', to: '/admin/billing/tenants', icon: Wallet }] },
    { label: 'Audit log', items: [{ label: 'Audit log', to: '/admin/audit-logs', icon: ScrollText }] },
    { label: 'Support inbox', items: [{ label: 'Support inbox', to: '/admin/support', icon: LifeBuoy }] },
    {
        label: 'Platform settings', items: [
            { label: 'Impersonation', to: '/admin/impersonate', icon: UserCheck },
            { label: 'Superadmin designation', to: '/admin/designate-superadmin', icon: Zap },
        ],
    },
];

const WORKSPACE_HOME = '/dashboard';

function PlatformLinks({ onNavigate }: { onNavigate?: () => void }) {
    const { pathname, search } = useLocation();
    const link = (item: PlatformItem) => {
        const active = isItemActive({ ...item, show: () => true }, pathname, search);
        const Icon = item.icon;
        return (
            <li key={item.to}>
                <Link
                    to={item.to}
                    className={`sidebar-item${active ? ' active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                    onClick={onNavigate}
                >
                    <Icon size={18} aria-hidden="true" /> {item.label}
                </Link>
            </li>
        );
    };
    return (
        <nav className="sidebar-nav" aria-label="Platform console">
            {PLATFORM_NAV.map((g) => g.items.length === 1 ? (
                <ul key={g.label} className="sidebar-list platform-single">{link(g.items[0])}</ul>
            ) : (
                <section key={g.label} className="sidebar-section platform-group">
                    <h2 className="sidebar-section-label">{g.label}</h2>
                    <ul className="sidebar-list">{g.items.map(link)}</ul>
                </section>
            ))}
        </nav>
    );
}

function BackToWorkspace({ onNavigate }: { onNavigate?: () => void }) {
    return (
        <Link to={WORKSPACE_HOME} className="sidebar-item platform-exit" onClick={onNavigate}>
            <ArrowLeft size={18} aria-hidden="true" /> Back to workspace
        </Link>
    );
}

/** The console label shown in the sidebar, the drawer and the topbar. */
function ConsoleLabel() {
    return <span className="platform-label">Platform console</span>;
}

/**
 * Platform shell: the superadmin operator console. Separate from the
 * workspace shell (Layout) so an operator always knows the context — its own
 * nav, a persistent "Platform console" label, the violet platform accent, and
 * a deliberate "Back to workspace" exit. Workspace chrome that acts on tenant
 * data (command palette, AI chat, notifications, support button) is not here.
 */
export function PlatformLayout() {
    const [menuOpen, setMenuOpen] = useState(false);
    const { pathname } = useLocation();
    const { theme } = useTheme();
    const close = () => setMenuOpen(false);

    useEffect(() => { setMenuOpen(false); }, [pathname]);

    return (
        <div className="app-shell shell shell--platform">
            <a className="shell-skip-link" href="#main-content">Skip to content</a>
            <ImpersonationBanner />
            <div className="shell-body">
                <aside className="sidebar">
                    <div className="sidebar-logo">
                        <KaziAtlasLogo size={46} />
                    </div>
                    <div className="platform-context"><ConsoleLabel /></div>
                    <PlatformLinks />
                    <div className="platform-exit-wrap"><BackToWorkspace /></div>
                    <AccountPanel />
                </aside>
                <div className="shell-main">
                    <header className="shell-topbar platform-topbar">
                        <button
                            type="button"
                            className="shell-icon-btn shell-menu-btn"
                            onClick={() => setMenuOpen(true)}
                            aria-label="Open navigation menu"
                            aria-haspopup="dialog"
                            aria-expanded={menuOpen}
                        >
                            <Menu size={20} aria-hidden="true" />
                        </button>
                        <span className="shell-topbar-logo">
                            <KaziAtlasLogo size={28} onLight={theme === 'light'} />
                        </span>
                        <div className="shell-workspace"><ConsoleLabel /></div>
                        <Link to={WORKSPACE_HOME} className="platform-topbar-exit" aria-label="Back to workspace">
                            <ArrowLeft size={16} aria-hidden="true" />
                            <span className="platform-topbar-exit-text" aria-hidden="true">Back to workspace</span>
                        </Link>
                    </header>
                    <main id="main-content" className="main-content" tabIndex={-1}>
                        <Outlet />
                    </main>
                </div>
            </div>
            <Drawer open={menuOpen} onClose={close} title="Platform console" side="left" className="shell-drawer shell-drawer--platform">
                <PlatformLinks onNavigate={close} />
                <div className="platform-exit-wrap"><BackToWorkspace onNavigate={close} /></div>
                <AccountPanel onNavigate={close} />
            </Drawer>
        </div>
    );
}
